import type { PropsWithChildren } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../../store/store';
import { FuuzeLink } from '../../fuuze-link';
import { FuuzeNavLink } from '../fuuze-nav-link';

type SearchNavLinkProps = PropsWithChildren<{
  title?: string;
  onClick?: () => void;
}>;

export function SearchNavLink({ title, children, onClick }: SearchNavLinkProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [lastSearchNavigation, setLastSearchNavigation] = useStore(
    useShallow(state => [state.lastSearchNavigation, state.setLastSearchNavigation]),
  );

  const onSearchLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    if (pathname === '/search') {
      // We are already on the search page, just clear the search bar and focus it
      navigate('/search', { replace: true, state: { search: { focusSearchBar: true, clear: true } } });
      return;
    }

    if (pathname.startsWith('/podcast')) {
      // We are in the "search context", meaning we need to go back to the search page without removing results
      navigate('/search');
      setLastSearchNavigation(undefined);
      return;
    }

    if (lastSearchNavigation) {
      // We are in another page, when clicking on the search we should come back to the last page displayed in the "search context"
      navigate(lastSearchNavigation);
      setLastSearchNavigation(undefined);
      return;
    }

    navigate('/search', { state: { search: { focusSearchBar: true, clear: true } } });
    onClick?.();
  };

  if (children) {
    return (
      <FuuzeLink to='#' onClick={onSearchLinkClick}>
        {children}
      </FuuzeLink>
    );
  }

  return (
    <FuuzeNavLink
      onClick={onSearchLinkClick}
      active={pathname.startsWith('/search') || pathname.startsWith('/podcast')}
      Icon={IoSearchOutline}
      title={title}
      rel='search'
    />
  );
}
